import { DispatchAction } from "@iolabs/redux-utils";
import React, { ReactNode, useEffect } from "react";
import { useDispatch } from "react-redux";
import { IElementsPosition, PositionType } from "../../../packages/Api/data/elements/types";
import {
    onAttachedEquipment,
    onAttachedGraphics,
    onAttachedMaterial,
    onAttachedWork,
    onAttribute,
    onBasicPositions,
    onDerivedPositions,
    onElementQuery,
    onElementQueryOperators,
    onUnitPrice,
    useDetailPosition,
} from "../../../redux/mapping";
import { useActiveProject } from "../../../redux/project";
import { ProjectData } from "../../ProjectSelectorWrapper/type";
import { MappingStep } from "../MappingDetailView/type";
import StepAttribute from "../StepAttribute/StepAttribute";
import StepElement from "../StepElement/StepElement";
import StepWork from "../StepWork/StepWork";
import { Alert } from "@material-ui/lab";
import { useIntl } from "react-intl";
import messages from "./messages";
import { useSecurityContext } from "../../../redux/keyclock/securityContext";

interface IStepContentWrapperProps {
    activeStep: number;
}

const StepContentWrapper: React.FC<IStepContentWrapperProps> = ({ activeStep }) => {
    const { tokenInitialized } = useSecurityContext();
    const dispatch = useDispatch<DispatchAction>();
    const activeProject: ProjectData | undefined = useActiveProject();
    const detailPosition: IElementsPosition | undefined = useDetailPosition();

    // translations
    const intl = useIntl();
    const transErrorStepNotValidType = intl.formatMessage({...messages.errorStepNotValidType})
    const transErrorUnknownStep = intl.formatMessage({...messages.errorUnknownStep})

    useEffect(() => {
        if (!activeProject || !detailPosition) return;

        // step attribute
        dispatch(
            onAttribute({
                projectID: activeProject?.projectID as number,
                positionID: detailPosition?.positionID as number,
            })
        );

        // element query
        dispatch(
            onElementQuery({
                projectID: activeProject?.projectID as number,
                positionID: detailPosition?.positionID as number,
            })
        );

        // element query operators
        dispatch(onElementQueryOperators({ }));

        // derived positions
        dispatch(
            onDerivedPositions({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // basic positions
        dispatch(
            onBasicPositions({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // attached graphics
        dispatch(
            onAttachedGraphics({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // attached work
        dispatch(
            onAttachedWork({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // attached equipment
        dispatch(
            onAttachedEquipment({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // attached material
        dispatch(
            onAttachedMaterial({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );

        // unit price
        dispatch(
            onUnitPrice({
                projectID: activeProject?.projectID,
                positionID: detailPosition?.positionID,
            })
        );
    }, [tokenInitialized, activeProject, detailPosition]);

    const getStepContent = (step: MappingStep): ReactNode => {
        switch (step) {
            case MappingStep.ELEMENT:
                return (
                    <StepElement />
                );
            case MappingStep.ATTRIBUTE:
                return detailPosition?.type != PositionType.MANUAL ? (
                    <StepAttribute />

                ) : (
                    <>
                        <Alert severity={"error"} >{transErrorStepNotValidType}</Alert>
                    </>
                );
            case MappingStep.MATERIAL:
                return detailPosition?.type != PositionType.MANUAL ? (
                    <StepWork />
                ) : (
                    <>
                        <Alert severity={"error"} >{transErrorStepNotValidType}</Alert>
                    </>
                );
            default:
                return <Alert severity={"error"} >{transErrorUnknownStep}</Alert>;
        }
    };

    return <>{getStepContent(activeStep)}</>;
};

export default StepContentWrapper;
